import { LinkWrap } from '@/components/blocks/Linkable';
import { headerNavBarDropMenuItemSX } from '@/components/content/Header/styles/navBar/dropMenuItem';
import { PageLink } from '@/data/Navigation';
import { Box, MenuItem, MenuList, Stack, Grid } from '@mui/material';
import { FC, useMemo } from 'react';

type JSXChildren = JSX.Element[] | JSX.Element;
const MAX_LEVELS = 3;

export const HeaderNavBarMenuList: FC<{
	tree?: PageLink[];
	display?: 'vertical' | 'horizontal';
	level?: number;
	parentUrl?: string;
	url?: string;
	handleToolTip?: (open: string) => void;
}> = ({
	tree,
	display = 'horizontal',
	level = 1,
	parentUrl = '',
	url: uri = '',
	handleToolTip,
}) => {
	const Element = useMemo(
		() =>
			level < MAX_LEVELS && tree?.some(({ children }) => children.length > 0)
				? (props: { children: JSXChildren }) => (
						<Stack
							justifyContent="flex-start"
							alignItems="flex-start"
							direction={display === 'horizontal' ? 'row' : 'column'}
							spacing={display === 'horizontal' ? 10 : ''}
							sx={{ marginTop: '26px' }}
							{...props}
						/>
				  )
				: (props: { children: JSXChildren }) => <MenuList {...props} />,
		[display, tree, level]
	);

	// Función para dividir los elementos en 3 columnas
	const chunkArray = (array: any[], chunkSize: number) => {
		const result = [];
		// eslint-disable-next-line functional/no-loop-statement
		for (let i = 0; i < array.length; i += chunkSize) {
			result.push(array.slice(i, i + chunkSize));
		}
		return result;
	};

	const columns = chunkArray(tree || [], Math.ceil((tree?.length || 0) / 3));

	return level <= MAX_LEVELS && tree?.length ? (
		<Element>
			{tree.length > 10 ? (
				<Grid container spacing={8}>
					{columns.map((column, columnIndex) => (
						<Grid item xs={4} key={columnIndex}>
							{column.map(({ label, url, children }) => {
								const base = ''; // level === 1 ? uri : parentUrl;
								const fullUrl = `${base}/${url}`.replace(/\/+/g, '/');
								return (
									<Box
										key={fullUrl}
										onClick={(e: React.MouseEvent<HTMLDivElement>) => {
											e.preventDefault();
											handleToolTip?.('close');
										}}
									>
										<LinkWrap href={fullUrl}>
											<MenuItem
												disableRipple
												component="a"
												sx={headerNavBarDropMenuItemSX({ isParent: children.length > 0 })}
												data-testid={`header-link-${label}`}
												id={`header-link-${label}`}
											>
												{label}
											</MenuItem>
										</LinkWrap>
										<HeaderNavBarMenuList
											tree={children}
											display={display}
											level={level + 1}
											parentUrl={fullUrl}
											url={uri}
										/>
									</Box>
								);
							})}
						</Grid>
					))}
				</Grid>
			) : (
				<Element>
					{tree.map(({ label, url, children }) => {
						const base = ''; // level === 1 ? uri : parentUrl;
						const fullUrl = `${base}/${url}`.replace(/\/+/g, '/');
						return (
							<Box
								key={fullUrl}
								onClick={(e: React.MouseEvent<HTMLDivElement>) => {
									e.preventDefault();
									handleToolTip?.('close');
								}}
							>
								<LinkWrap href={fullUrl}>
									<MenuItem
										disableRipple
										component="a"
										sx={headerNavBarDropMenuItemSX({ isParent: children.length > 0 })}
										data-testid={`header-link-${label}`}
										id={`header-link-${label}`}
									>
										{label}
									</MenuItem>
								</LinkWrap>
								<HeaderNavBarMenuList
									tree={children}
									display={display}
									level={level + 1}
									parentUrl={fullUrl}
									url={uri}
									handleToolTip={handleToolTip}
								/>
							</Box>
						);
					})}
				</Element>
			)}
		</Element>
	) : null;
};
